import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useUnsavedChangesGuard } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { useDineCourses, useDineMenuGroups } from 'hooks/api/useDineOps';
import { saveReportFile, blobErrorMessage } from 'utils/reportFile';
import { STORAGE_KEYS } from 'auth/storageKeys';
import { DINE_MODULES } from 'components/modules/dineops-module/dineOpsScreens';
import type { PriceBoardImportResult, PriceBoardOutlet, PriceBoardQuery, PriceBoardRow } from 'api/types';
import DineOpsPriceBoardBulkModal from 'components/modals/modules/dineops-module/DineOpsPriceBoardBulkModal';
import DineOpsPriceBoardSaveModal from 'components/modals/modules/dineops-module/DineOpsPriceBoardSaveModal';
import DineOpsPriceBoardImportModal from 'components/modals/modules/dineops-module/DineOpsPriceBoardImportModal';
import PriceCell, { changedBg } from './PriceCell';
import {
  MENU, applyPlan, boardMenuPrice, boardValue, cellKey, isPricedRow, setCell,
  type BoardColumn, type PendingMap,
} from './priceBoardMath';

/**
 * Price Board (MENU-010; crs-doc DineOps/DineOps-Item-Pricing.md P1, D1 and D2).
 *
 * Dishes down, outlets across, every cell editable. The Menu column is the menu
 * price; an outlet cell is that outlet's own price, and an empty (grey ·) cell
 * follows the menu price. A dish with sizes is priced on its size rows; an
 * open-priced dish is priced when ordered; a dash means the outlet does not sell
 * the dish (Outlet Items lists dishes in an outlet).
 *
 * Changes stay on the board, across pages and filters, until Save, which asks for
 * one reason and saves each cell. A cell someone else changed meanwhile is
 * listed and not saved. Not bound to the header outlet: every outlet the user
 * works in is a column.
 */

interface Filters { courseId?: string; groupId?: string; outletIds?: string[]; onlyDiffering?: string; includeInactive?: string }

type Row = PriceBoardRow & { id: string };

/** Most dishes the bulk change or "every dish matching" loads. */
const MAX_MATCHING = 5000;

/**
 * The menu price is charged by every outlet that follows it, so changing it needs the
 * Menu Items edit right as well (the server checks it too). Read from the role matrix.
 */
const canEditMenuPrice = (): boolean => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
    return Array.isArray(all?.[DINE_MODULES.MENU]) && all[DINE_MODULES.MENU].includes('update');
  } catch {
    return false;
  }
};


// ---------------------------------------------------------------- the screen

const DineOpsPriceBoardScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const canEdit = allowedActions.includes('edit');
  const canExport = allowedActions.includes('export');
  const canEditMenu = useMemo(() => canEdit && canEditMenuPrice(), [canEdit]);

  const [outlets, setOutlets] = useState<PriceBoardOutlet[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [query, setQuery] = useState<PriceBoardQuery>({ page: 1, pageSize: 25 });

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingMap>(new Map());
  const [selected, setSelected] = useState<Map<string, Row>>(new Map());
  /** Bumped to untick every row: the grid keeps its own ticks, so it is remounted. */
  const [gridKey, setGridKey] = useState(0);
  const clearSelection = useCallback(() => { setSelected(new Map()); setGridKey((k) => k + 1); }, []);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ result: PriceBoardImportResult; fileName: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useUnsavedChangesGuard({ isDirty: pending.size > 0, message: 'The Price Board has unsaved changes.' });

  const { data: courses } = useDineCourses({ pageSize: 100 });
  const { data: groups } = useDineMenuGroups({ pageSize: 200 });

  useEffect(() => {
    dineOpsService.getPriceBoardOutlets()
      .then(setOutlets)
      .catch((err: any) => setLoadError(err?.response?.data?.message ?? 'The outlets could not be loaded.'));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQuery((q) => ({ ...q, searchTerm: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setQuery((q) => ({
      ...q,
      page: 1,
      courseId: filters.courseId || undefined,
      groupId: filters.groupId || undefined,
      outletIds: filters.outletIds?.length ? filters.outletIds.join(',') : undefined,
      onlyDiffering: filters.onlyDiffering === 'true' || undefined,
      includeInactive: filters.includeInactive === 'true' || undefined,
    }));
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await dineOpsService.getPriceBoard(query);
      setRows(res.data.map((r) => ({ ...r, id: r.itemId })));
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? 'The Price Board could not be loaded.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  /** The outlet columns shown: the filter's, else all, in the outlets' order. */
  const shownOutlets = useMemo(() => (filters.outletIds?.length
    ? outlets.filter((o) => filters.outletIds!.includes(o.id))
    : outlets), [outlets, filters.outletIds]);

  const boardColumns = useMemo(() => [
    { id: MENU as BoardColumn, name: 'Menu' },
    ...shownOutlets.map((o) => ({ id: o.id as BoardColumn, name: o.outletName })),
  ], [shownOutlets]);

  const commit = useCallback((row: PriceBoardRow, column: BoardColumn, name: string, value: number | null) => {
    setPending((p) => setCell(p, row, column, name, value));
  }, []);

  // ---- selection: kept across pages; ticking a dish with sizes takes its sizes
  const handleSelection = useCallback((picked: Row[]) => {
    setSelected((prev) => {
      const next = new Map(prev);
      rows.forEach((r) => next.delete(r.itemId));
      picked.forEach((r) => {
        next.set(r.itemId, r);
        if (r.sizeCount > 0) rows.filter((s) => s.parentItemId === r.itemId).forEach((s) => next.set(s.itemId, s));
      });
      return next;
    });
  }, [rows]);

  const loadMatching = useCallback(async (): Promise<PriceBoardRow[]> => {
    if (total > MAX_MATCHING) throw new Error(`${total} dishes match; a change covers at most ${MAX_MATCHING}. Filter by category or group.`);
    const all: PriceBoardRow[] = [];
    for (let page = 1; (page - 1) * 100 < total; page++) {
      const res = await dineOpsService.getPriceBoard({ ...query, page, pageSize: 100 });
      all.push(...res.data);
      if (res.data.length === 0) break;
    }
    return all;
  }, [query, total]);

  const handleExport = useCallback(async () => {
    setBusy(true);
    try {
      const { blob, disposition } = await dineOpsService.exportPriceBoard({ ...query, page: 1 });
      saveReportFile(blob, disposition, `PriceBoard_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      notificationService.toast.error(await blobErrorMessage(err, 'Export failed.'));
    } finally { setBusy(false); }
  }, [query]);

  const handleImportFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const result = await dineOpsService.importPriceBoard(file);
      setImportResult({ result, fileName: file.name });
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The file could not be read.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }, []);

  const applyImport = useCallback(() => {
    if (!importResult) return;
    setPending((p) => {
      const next = new Map(p);
      importResult.result.changes.forEach((c) => next.set(cellKey(c.itemId, c.outletId ?? MENU), {
        itemId: c.itemId, outletId: c.outletId ?? null, itemName: c.itemName, column: c.column,
        before: c.before ?? null, after: c.after ?? null, rowVersion: c.rowVersion,
      }));
      return next;
    });
    notificationService.toast.info(`${importResult.result.changes.length} price change(s) are on the board. Check them, then Save.`);
    setImportResult(null);
  }, [importResult]);

  const columns = useMemo((): SyutGridColumn<Row>[] => {
    const cols: SyutGridColumn<Row>[] = [
      { id: 'itemName', header: 'Dish', accessorKey: 'itemName', enableSorting: false, size: 240,
        cell: ({ row }) => {
          const r = row.original;
          const name = r.parentItemId ? <span style={{ paddingLeft: 18 }}>↳ {r.variantName}</span> : <span>{r.itemName}</span>;
          return (
            <div title={`${r.itemCode ?? ''} · ${r.courseName ?? ''} › ${r.groupName ?? ''}`} style={{ opacity: r.isActive ? 1 : 0.6 }}>
              {name}
              {!r.parentItemId && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.itemCode} · {r.groupName}</div>}
            </div>
          );
        } },
    ];

    boardColumns.forEach((col, colIndex) => {
      const isMenu = col.id === MENU;
      cols.push({
        id: isMenu ? 'menu' : `o_${col.id}`,
        header: col.name,
        enableSorting: false,
        size: isMenu ? 120 : 130,
        cell: ({ row }) => {
          const r = row.original;
          if (r.sizeCount > 0) return isMenu ? <span style={{ color: 'var(--text-muted)' }}>by size</span> : null;
          if (r.isOpenPrice) return isMenu ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>open price</span> : null;
          const value = boardValue(r, col.id, pending);
          if (value === undefined) {
            return <span title={`Not sold in ${col.name}. Add it on Outlet Items.`} style={{ color: 'var(--text-muted)', display: 'block', textAlign: 'center' }}>—</span>;
          }
          return (
            <PriceCell
              value={value}
              followsPrice={isMenu ? undefined : boardMenuPrice(r, pending)}
              isMenu={isMenu}
              changed={pending.has(cellKey(r.itemId, col.id))}
              editable={isMenu ? canEditMenu : canEdit}
              rowIndex={row.index}
              colIndex={colIndex}
              label={`${r.itemName}, ${col.name}`}
              onCommit={(v) => commit(r, col.id, col.name, v)}
            />
          );
        },
      });
    });
    return cols;
  }, [boardColumns, pending, canEdit, canEditMenu, commit]);

  /** The bulk change offers the Menu column only to those who may change the menu price. */
  const bulkColumns = useMemo(() => (canEditMenu ? boardColumns : boardColumns.filter((c) => c.id !== MENU)), [boardColumns, canEditMenu]);

  const pendingList = useMemo(() => Array.from(pending.values()), [pending]);
  const selectedRows = useMemo(() => Array.from(selected.values()), [selected]);
  const pricedSelected = selectedRows.filter(isPricedRow).length;

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canEdit) b.push({ id: 'bulk', icon: <IconSystem.percent size={16} />, label: 'Change prices…', variant: 'secondary',
      tooltip: pricedSelected ? `Change the ${selected.size} ticked row(s), or every dish matching the filters` : 'Change every dish matching the filters, or tick rows first',
      onClick: () => setBulkOpen(true), disabled: busy || total === 0 });
    if (canEdit && pending.size > 0) {
      b.push({ id: 'discard', icon: <IconSystem.x size={16} />, label: 'Discard', variant: 'secondary', onClick: () => setDiscardOpen(true), disabled: busy });
      b.push({ id: 'save', icon: <IconSystem.save size={16} />, label: `Save… (${pending.size})`, variant: 'primary', onClick: () => setSaveOpen(true), disabled: busy });
    }
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh data (unsaved changes stay)', variant: 'icon' });
    if (canExport) b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: () => void handleExport(), tooltip: 'Export to Excel', variant: 'icon', disabled: busy });
    if (canEdit) b.push({ id: 'import', icon: <IconSystem.upload size={18} />, label: '', onClick: () => fileInput.current?.click(), tooltip: 'Import from Excel (shows a preview; nothing is saved on its own)', variant: 'icon', disabled: busy });
    return b;
  }, [canEdit, canExport, pending.size, busy, total, load, handleExport, pricedSelected, selected.size]);

  const courseOptions = useMemo(() => courses.map((c) => ({ value: String(c.id), label: c.courseName })), [courses]);
  const groupOptions = useMemo(() => {
    const visible = filters.courseId ? groups.filter((g) => String(g.courseId) === filters.courseId) : groups;
    return visible.map((g) => ({ value: String(g.id), label: `${g.courseName} · ${g.groupName}` }));
  }, [groups, filters.courseId]);
  const outletOptions = useMemo(() => outlets.map((o) => ({ value: o.id, label: o.outletName })), [outlets]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Price Board"
        description="Every dish's price in every outlet. An empty outlet cell follows the menu price. Nothing is saved until you click Save."
        icon={<IconSystem.dollarSign size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search dish, code, group or category' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      <input ref={fileInput} type="file" accept=".xlsx" style={{ display: 'none' }}
        onChange={(e) => void handleImportFile(e.target.files?.[0])} />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', padding: '4px 10px', fontSize: 12,
              color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>
              <span><span style={{ color: 'var(--text-muted)' }}>180.00 ·</span> grey = follows the menu price</span>
              <span><strong style={{ color: 'var(--text-primary)' }}>280.00</strong> = the outlet's own price{canEdit ? ' (↺ follows the menu again)' : ''}</span>
              <span>— = not sold in that outlet</span>
              {canEdit && !canEditMenu && <span>The Menu column needs the edit right on Menu Items.</span>}
            </div>
            {(pending.size > 0 || selected.size > 0) && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '6px 10px', fontSize: 13,
                borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                {selected.size > 0 && (
                  <span>
                    {selected.size} row{selected.size === 1 ? '' : 's'} ticked
                    {' '}<button className="btn btn-link btn-sm" style={{ padding: 0 }} onClick={clearSelection}>clear</button>
                  </span>
                )}
                {pending.size > 0 && (
                  <span>
                    <span style={{ background: changedBg, padding: '0 6px', borderRadius: 3 }}>{pending.size} unsaved change{pending.size === 1 ? '' : 's'}</span>
                    {' '}Changed cells are highlighted. Click Save to keep them.
                  </span>
                )}
              </div>
            )}
            <SyutGrid
              key={gridKey}
              data={rows}
              columns={columns}
              enableSorting={false}
              enableRowSelection={canEdit}
              onSelectionChange={handleSelection}
              enablePagination
              manualPagination
              totalCount={total}
              currentPage={(query.page ?? 1) - 1}
              pageSize={query.pageSize ?? 25}
              onPageChange={(i: number) => setQuery((q) => ({ ...q, page: i + 1 }))}
              onPageSizeChange={(size: number) => setQuery((q) => ({ ...q, pageSize: size, page: 1 }))}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenLeftColumns={canEdit ? ['select', 'itemName'] : ['itemName']}
              isLoading={loading}
              paginationInfo={`Showing ${rows.filter((r) => !r.parentItemId).length} of ${total} dish${total === 1 ? '' : 'es'}`}
              emptyMessage={loadError ?? (filters.onlyDiffering === 'true'
                ? 'No dish has an outlet price that differs from the menu price.'
                : 'No dishes match. Dishes are added on Menu Items.')}
            />
          </div>
        </div>

        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Category" options={courseOptions} value={filters.courseId ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, courseId: (v as string) || undefined, groupId: undefined }))}
                placeholder="All categories" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Menu group" options={groupOptions} value={filters.groupId ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, groupId: (v as string) || undefined }))}
                placeholder="All groups" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Outlets" isMulti options={outletOptions} value={filters.outletIds ?? []}
                onChange={(v) => setFilters((p) => ({ ...p, outletIds: Array.isArray(v) && v.length ? (v as string[]) : undefined }))}
                placeholder="Every outlet" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Only differing" options={[{ value: 'true', label: 'Yes' }]} value={filters.onlyDiffering ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, onlyDiffering: (v as string) || undefined }))}
                placeholder="All dishes" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Include switched off" options={[{ value: 'true', label: 'Yes' }]} value={filters.includeInactive ?? ''}
                onChange={(v) => setFilters((p) => ({ ...p, includeInactive: (v as string) || undefined }))}
                placeholder="Active dishes only" size="sm" isClearable />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setFilters({}); setSearchQuery(''); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <DineOpsPriceBoardBulkModal
        isOpen={bulkOpen}
        selectedRows={selectedRows}
        matchingCount={total}
        loadMatching={loadMatching}
        columns={bulkColumns}
        pending={pending}
        onClose={() => setBulkOpen(false)}
        onApply={(lines) => {
          setPending((p) => applyPlan(p, lines));
          notificationService.toast.info(`${lines.length} price change(s) are on the board. Check them, then Save.`);
        }}
      />
      <DineOpsPriceBoardSaveModal
        isOpen={saveOpen}
        changes={pendingList}
        onClose={() => setSaveOpen(false)}
        onSaved={(res) => {
          setPending(new Map());
          clearSelection();
          if (res.notSaved.length === 0) {
            setSaveOpen(false);
            notificationService.toast.success(`${res.saved} price${res.saved === 1 ? '' : 's'} saved.`);
          }
          void load();
        }}
      />
      <DineOpsPriceBoardImportModal
        result={importResult?.result ?? null}
        fileName={importResult?.fileName ?? ''}
        onClose={() => setImportResult(null)}
        onApply={applyImport}
      />
      <ConfirmDialog
        open={discardOpen}
        title="Discard unsaved changes?"
        message={`${pending.size} price change${pending.size === 1 ? '' : 's'} on the board will be lost. Saved prices are not affected.`}
        confirmLabel="Discard"
        cancelLabel="Keep them"
        variant="danger"
        onConfirm={() => { setPending(new Map()); setDiscardOpen(false); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </div>
  );
};

export default DineOpsPriceBoardScreen;
